import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos, categories } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

function getArticleSchema(item: { title: string; description?: string; author?: string; date?: string; thumbnail: string; category: string }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: item.title,
        description: 'description' in item ? item.description : item.title,
        author: { '@type': 'Person', name: 'author' in item && item.author ? item.author : '91在线视频' },
        datePublished: 'date' in item && item.date ? item.date : new Date().toISOString().split('T')[0],
        image: item.thumbnail,
        publisher: { '@type': 'Organization', name: '91在线视频' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91onlinevideo.com/' },
          { '@type': 'ListItem', position: 2, name: item.category, item: `https://www.91onlinevideo.com/category/${item.category}` },
          { '@type': 'ListItem', position: 3, name: item.title },
        ],
      },
    ],
  };
}

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const videoArticle = videos.find((v) => v.id === id);
  const textArticle = articles.find((a) => a.id === id);
  const item = videoArticle || textArticle;

  if (!item) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-400 text-lg">内容未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-400 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isVideo = 'duration' in item;
  const categoryName = categories.find((c) => c.slug === item.category)?.name || item.category;
  const relatedVideos = videos
    .filter((v) => v.category === item.category && v.id !== item.id)
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getArticleSchema(item)) }} />

      <main className="pt-16">
        <div className="pt-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: categoryName, to: `/category/${item.category}` },
              { label: item.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 lg:px-8 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            {/* Video/Article Header */}
            <header className="mb-6">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 leading-tight">
                {item.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-foreground-400">
                {'author' in item && (
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {item.author}
                  </span>
                )}
                {'date' in item && (
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {item.date}
                  </span>
                )}
                {'views' in item && (
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {item.views >= 10000 ? `${(item.views / 10000).toFixed(1)}万次播放` : `${item.views}次播放`}
                  </span>
                )}
                {'rating' in item && (
                  <span className="flex items-center gap-1 text-accent-500">
                    <i className="ri-star-fill"></i>
                    {item.rating} 分
                  </span>
                )}
                {'duration' in item && (
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {item.duration}
                  </span>
                )}
                <span className="px-2 py-0.5 bg-accent-100 text-accent-800 text-xs rounded whitespace-nowrap">
                  {categoryName}
                </span>
              </div>
            </header>

            {/* Thumbnail / Video Player Placeholder */}
            <div className="relative aspect-video rounded-lg overflow-hidden mb-6 bg-background-100">
              <img
                src={item.thumbnail}
                alt={item.title}
                title={item.title}
                className="w-full h-full object-cover"
              />
              {isVideo && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group cursor-pointer">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white group-hover:bg-primary-500 transition-all group-hover:scale-110">
                    <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full border border-background-200/30 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Description / Content */}
            {'description' in item && (
              <div className="bg-background-100 rounded-lg p-4 md:p-6 mb-8 border border-background-200/30">
                <h3 className="text-sm font-semibold text-foreground-700 mb-2">简介</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
              </div>
            )}

            {'content' in item && (
              <div
                className="prose prose-invert max-w-none text-foreground-600 leading-relaxed text-sm md:text-base [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-800 [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:mb-4"
                dangerouslySetInnerHTML={{ __html: item.content }}
              />
            )}
          </div>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section className="mt-10 md:mt-14 pt-8 border-t border-background-200/30 max-w-4xl mx-auto">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {relatedVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}